import { sessionService } from "@services/sessionService";
import { tokenStore } from "@services/tokenStore";
import { config } from "../v3/config";

// The form engine's HTTP edge lives in wingspan-api (the forms service itself
// is gRPC-only). This client targets the V3 surface —
// /v3/compliance/forms/{formId}/(schema|evaluate|submit|pdf) and /checklist,
// the collected-form variants marked by `payeeEngagementId`
// (wingspan-api #493 stack; PDF variant #498).
//
// TRANSITIONAL: the generated @wingspanhq/wingspan-api-sdk does not carry
// these operations yet — its FormsApi is the compliance/W-9 domain only.
// Once the SDK regenerates from the merged V3 spec, replace this hand-rolled
// client + src/services/employeeTaxForms/* with a createV3Queries registry
// over formsApi (see docs/v3-queries.md) and drop the hand-written
// request/response types in modules/EmployeeTaxForms/types.ts.
const BASE_PATH = "/v3/compliance/forms";

export type FormsRequestOptions = {
  method: "GET" | "POST";
  /** Path under /v3/compliance/forms, e.g. `/${formId}/schema`. */
  path: string;
  query?: Record<string, string | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  responseType?: "json" | "blob";
};

const buildUrl = (
  path: string,
  query?: Record<string, string | undefined>
): string => {
  const url = new URL(`${config.apiBaseUrl}${BASE_PATH}${path}`);
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined) url.searchParams.set(key, value);
  });
  return url.toString();
};

/**
 * V3 problem+json (RFC 9457) → the axios-shaped envelope the DynamicForm
 * engine parses (extractApiErrorInfo / extractServerValidationErrors):
 * - `detailCode` "forms.StaleVersion" → data.errorType "StaleVersion" (both
 *   engine 409s ride the same status; the code is the only discriminator)
 * - `errors` [{field, code, message}] → data.validationErrors
 *   [{fieldKey, ruleKind, message}]
 * - `detail`/`title` → data.error/message
 */
const toWSServiceErrorShape = async (response: Response): Promise<Error> => {
  let problemBody:
    | {
        code?: string;
        title?: string;
        detail?: string;
        detailCode?: string;
        errors?: Array<{ field: string; code: string; message?: string }>;
        extensions?: Record<string, unknown>;
      }
    | undefined;
  try {
    problemBody = await response.json();
  } catch {
    // Non-JSON body (e.g. a gateway HTML error page) — status-only error.
  }
  const detailCode = problemBody?.detailCode;
  const errorType = detailCode?.startsWith("forms.")
    ? detailCode.slice("forms.".length)
    : problemBody?.code;
  const message =
    problemBody?.detail ??
    problemBody?.title ??
    `Request failed with status ${response.status}`;
  const validationErrors = Array.isArray(problemBody?.errors)
    ? problemBody?.errors.map(fieldError => ({
        fieldKey: fieldError.field,
        ruleKind: fieldError.code,
        message: fieldError.message
      }))
    : undefined;

  const error = new Error(message) as Error & { response?: unknown };
  error.response = {
    status: response.status,
    data: {
      errorType,
      error: message,
      message,
      ...(validationErrors ? { validationErrors } : {}),
      // Engine extras (currentVersion, blockedBy) ride problem `extensions`.
      ...(problemBody?.extensions ?? {})
    }
  };
  return error;
};

const doFetch = (
  url: string,
  options: FormsRequestOptions
): Promise<Response> =>
  fetch(url, {
    method: options.method,
    headers: {
      Authorization: `Bearer ${tokenStore.getAccessToken() ?? ""}`,
      ...(options.body !== undefined
        ? { "Content-Type": "application/json" }
        : {}),
      ...options.headers
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  });

/**
 * Authorized fetch against the V3 forms surface. Mirrors the SDK's
 * authRefreshMiddleware convention: one refresh + retry on 401, clearing the
 * token store when the refresh itself fails.
 */
export const requestForms = async <T = unknown>(
  options: FormsRequestOptions
): Promise<T> => {
  const url = buildUrl(options.path, options.query);
  let response = await doFetch(url, options);
  if (response.status === 401) {
    try {
      await sessionService.refreshAccessToken();
    } catch {
      tokenStore.clear();
      throw await toWSServiceErrorShape(response);
    }
    response = await doFetch(url, options);
  }
  if (!response.ok) {
    throw await toWSServiceErrorShape(response);
  }
  return (
    options.responseType === "blob" ? response.blob() : response.json()
  ) as Promise<T>;
};
